import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * A short description shown above its child on hover and keyboard focus (WCAG 1.4.13: it stays while hovered, Escape
 * dismisses it). The text is a `role="tooltip"` element that describes the child's first focusable element
 * (`aria-describedby`); a child with nothing focusable (a chip, an icon) becomes a tab stop itself. `text` keeps its
 * line breaks.
 */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [ownStop, setOwnStop] = useState(false);
  useLayoutEffect(() => {
    const target = ref.current?.querySelector<HTMLElement>(FOCUSABLE);
    setOwnStop(!target);
    if (!target) return;
    const before = target.getAttribute('aria-describedby');
    target.setAttribute('aria-describedby', [before, id].filter(Boolean).join(' '));
    return () => {
      if (before) target.setAttribute('aria-describedby', before);
      else target.removeAttribute('aria-describedby');
    };
  }, [id, children]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <span
      ref={ref}
      className={`nyk-ui-tip${open ? ' nyk-ui-tip-open' : ''}`}
      tabIndex={ownStop ? 0 : undefined}
      aria-describedby={ownStop ? id : undefined}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setOpen(false)}
    >
      {children}
      <span role="tooltip" id={id} className="nyk-ui-tip-text">
        {text}
      </span>
    </span>
  );
}
